import api from './api';
import { ApiResponse, Group, Grade } from '../types';

export interface GroupFormData {
  name: string;
  description?: string;
  centerId?: string;
  isActive?: boolean;
}

export interface GradeFormData {
  name: string;
  minPercentage: number;
  maxPercentage: number;
  color: string;
  schoolId?: string;
  isActive?: boolean;
}

/** How the school's exam results are decided (Settings → Results). */
export interface ResultSettings {
  /** An absent paper counts as zero (and fails that subject), else it's left out. */
  absentCountsAsZero: boolean;
  /** Papers graded in this band or lower are failed; null = no pass/fail. */
  failingGradeId: string | null;
  /** 'overall': the overall % decides; 'every_subject': every paper must pass too. */
  passRule: 'overall' | 'every_subject';
}

class SettingsService {
  // Group management
  async getGroups(params?: {
    page?: number;
    limit?: number;
    search?: string;
    isActive?: boolean;
    centerId?: string;
  }) {
    const response = await api.get<ApiResponse<{
      groups: Group[];
      pagination: any;
    }>>('/settings/groups', { params });
    return response.data;
  }

  async getGroupById(id: string) {
    const response = await api.get<ApiResponse<Group>>(`/settings/groups/${id}`);
    return response.data;
  }

  async createGroup(data: GroupFormData) {
    const response = await api.post<ApiResponse<Group>>('/settings/groups', data);
    return response.data;
  }

  async updateGroup(id: string, data: GroupFormData) {
    const response = await api.put<ApiResponse<Group>>(`/settings/groups/${id}`, data);
    return response.data;
  }

  async deleteGroup(id: string) {
    const response = await api.delete<ApiResponse>(`/settings/groups/${id}`);
    return response.data;
  }

  // Grade management
  async getGrades(params?: {
    page?: number;
    limit?: number;
    search?: string;
    isActive?: boolean;
    schoolId?: string;
  }) {
    const response = await api.get<ApiResponse<{
      grades: Grade[];
      pagination: any;
    }>>('/settings/grades', { params });
    return response.data;
  }

  async getGradeById(id: string) {
    const response = await api.get<ApiResponse<Grade>>(`/settings/grades/${id}`);
    return response.data;
  }

  async createGrade(data: GradeFormData) {
    const response = await api.post<ApiResponse<Grade>>('/settings/grades', data);
    return response.data;
  }

  async updateGrade(id: string, data: GradeFormData) {
    const response = await api.put<ApiResponse<Grade>>(`/settings/grades/${id}`, data);
    return response.data;
  }

  async deleteGrade(id: string) {
    const response = await api.delete<ApiResponse>(`/settings/grades/${id}`);
    return response.data;
  }

  // Result rules. Saving re-scores every saved result on the server.
  async getResultSettings() {
    const response = await api.get<ApiResponse<ResultSettings>>('/settings/results');
    return response.data;
  }

  /** Saves a rule change; `updatedResults` is how many saved results it re-scored. */
  async updateResultSettings(data: Partial<ResultSettings>) {
    const response = await api.patch<ApiResponse<{ settings: ResultSettings; updatedResults: number }>>('/settings/results', data);
    return response.data;
  }

  // Class management
  async getClasses(params?: { page?: number; limit?: number; search?: string }) {
    const response = await api.get<ApiResponse<{ classes: any[]; pagination: any }>>('/administration/classes', { params });
    return response.data;
  }

  // Subject management
  async getSubjects(params?: { page?: number; limit?: number; search?: string; classId?: string }) {
    const response = await api.get<ApiResponse<{ subjects: any[]; pagination: any }>>('/administration/subjects', { params });
    return response.data;
  }

  // Section management  
  async getSections(params?: { page?: number; limit?: number; search?: string }) {
    const response = await api.get<ApiResponse<{ sections: any[]; pagination: any }>>('/administration/sections', { params });
    return response.data;
  }
}

export const settingsService = new SettingsService();
export default settingsService;
